import { useEffect, useState } from "react";
import ScoreChart from "./ScoreChart";
import { Loading } from "../Loading";
import { useMatchFilters } from "../../hooks/useMatchFilters";
import { useTeamStats } from "../../hooks/useTeamStats";
import { groupBy } from "../../utils/group-by";
import { NoDataAvailable } from "../NoDataAvailable";

export function Scores() {
  const { loading, data } = useTeamStats();

  const { selectedMachines, selectedPlayers, pickOrRespond, singlesOrDoubles } =
    useMatchFilters();

  function filterGroupAndSortScores() {
    if (!data) return [];

    const filteredScores = data.scores
      .filter((score) => {
        return score.rating != null;
      })
      .filter((score) => {
        const machineMatches = selectedMachines.includes(score.machineGroupId);
        const playerMatches = selectedPlayers.includes(score.playerId);
        const pickOrRespondMatches = pickOrRespond.includes(
          score.pickOrRespond
        );
        const singlesOrDoublesMatches = singlesOrDoubles.includes(
          score.singlesOrDoubles
        );

        return (
          machineMatches &&
          playerMatches &&
          pickOrRespondMatches &&
          singlesOrDoublesMatches
        );
      });

    const grouped = groupBy(filteredScores, (s) => s.machineGroupId);

    const groupedWithNames = Object.entries(grouped).map(
      ([machineGroupId, scores]) => {
        const machine = data.machines.find(
          (m) => m.machineGroupId === Number(machineGroupId)
        );
        return {
          machineGroupId: Number(machineGroupId),
          name: machine ? machine.machineGroupName : "Unknown Machine",
          scores,
        };
      }
    );

    const sorted = groupedWithNames.sort((a, b) => {
      return (
        selectedMachines.indexOf(a.machineGroupId) -
        selectedMachines.indexOf(b.machineGroupId)
      );
    });

    return sorted;
  }

  const [machineColors, setMachineColors] = useState<{ [key: number]: string }>(
    {}
  );

  useEffect(() => {
    if (!data) return;

    const colorPalette = [
      "#E6194B",
      "#3CB44B",
      "#0082C8",
      "#F58231",
      "#911EB4",
      "#46F0F0",
      "#F032E6",
      "#D2F53C",
      "#FABEBE",
      "#008080",
      "#E6BEFF",
      "#AA6E28",
      "#FFFAC8",
      "#800000",
      "#Aaffc3",
      "#808000",
      "#FFD8B1",
      "#000080",
      "#808080",
      "#FF0000",
      "#00FF00",
      "#0000FF",
      "#FF00FF",
    ];

    const colors: { [key: number]: string } = {};
    data.machines.forEach((machine, index) => {
      colors[machine.machineGroupId] =
        colorPalette[index % colorPalette.length];
    });
    setMachineColors(colors);
  }, [data]);

  const [scores, setScores] = useState(filterGroupAndSortScores());

  useEffect(() => {
    setScores(filterGroupAndSortScores());
  }, [
    loading,
    selectedMachines,
    selectedPlayers,
    pickOrRespond,
    singlesOrDoubles,
    data,
  ]);

  if (loading || !data) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-wrap items-start justify-center gap-4">
      {scores.length === 0 && <NoDataAvailable pluralNoun="scores" />}
      {scores.length > 0 &&
        scores.map((s) => {
          return (
            <div
              key={s.machineGroupId}
              className="w-full md:w-[48%] lg:w-[32%]"
            >
              <ScoreChart
                key={s.machineGroupId}
                data={s.scores}
                roster={data.roster}
                machineGroupName={s.name}
                machineColor={machineColors[s.machineGroupId]}
              />
            </div>
          );
        })}
    </div>
  );
}
